import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface QuickAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  onCreateAppointment: (date: Date) => void;
  onCreateTask: (date: Date) => void;
}

export const QuickAddModal = ({
  isOpen,
  onClose,
  selectedDate,
  onCreateAppointment,
  onCreateTask
}: QuickAddModalProps) => {
  const handleCreateAppointment = () => {
    onCreateAppointment(selectedDate);
    onClose();
  };

  const handleCreateTask = () => {
    onCreateTask(selectedDate);
    onClose();
  };

  const options = [
    {
      title: 'Compromisso',
      description: 'Audiência, reunião ou atendimento com cliente',
      icon: Calendar,
      tint: 'bg-[#c4ab83]/15 text-[#9a7f52]',
      onClick: handleCreateAppointment,
    },
    {
      title: 'Tarefa',
      description: 'Prazo ou atividade a cumprir',
      icon: Clock,
      tint: 'bg-slate-100 text-slate-600',
      onClick: handleCreateTask,
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md overflow-hidden border border-[#c4ab83]/30 bg-white p-0 shadow-2xl">
        <DialogHeader className="relative overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] px-6 py-5 text-left">
          <img
            src="/imgs/logos allana/letra_A_logo.png"
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-3 top-1/2 h-24 w-24 -translate-y-1/2 select-none object-contain opacity-[0.12]"
          />
          <p className="relative text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Adicionar</p>
          <DialogTitle className="relative mt-1 text-xl font-semibold tracking-tight text-slate-900">
            {format(selectedDate, "EEEE, dd 'de' MMMM", { locale: ptBR })}
          </DialogTitle>
          <DialogDescription className="relative text-sm text-slate-500">O que você quer criar neste dia?</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 p-4">
          {options.map(({ title, description, icon: Icon, tint, onClick }, index) => (
            <button
              key={title}
              type="button"
              onClick={onClick}
              className="group flex w-full items-center gap-4 rounded-xl border border-slate-200 p-4 text-left transition-all duration-200 animate-in fade-in slide-in-from-bottom-1 fill-mode-both hover:border-[#c4ab83]/70 hover:bg-[#faf7f2]"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tint}`}>
                <Icon className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-slate-900">{title}</span>
                <span className="block text-sm text-slate-500">{description}</span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-[#9a7f52]" />
            </button>
          ))}
        </div>

        <div className="flex justify-end border-t border-slate-100 px-4 py-3">
          <Button variant="ghost" onClick={onClose} className="text-slate-500 hover:text-slate-900">
            Cancelar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
